/**
 * The rising-iPhone voice surface — shell only, shared by every consumer.
 *
 * Two very different things render this handset:
 *
 *   1. `TalkToBriModal` on bizrnr.com — React, mounts `FloatingVoiceV1`
 *      directly into the stage.
 *   2. `/widget.js` in `data-modal="iphone"` mode — vanilla ES5 on a third
 *      party's page, mounts an `/embed/voice` iframe into the stage.
 *
 * They cannot share a component (one is React, one must run on any host page
 * however old) and they cannot share a `.css` file (a third-party page will
 * not import our stylesheet, and a second network request for an embed is a
 * cost we do not want). So the shell lives here, as a string, and both sides
 * consume THIS — the React modal injects it, the loader inlines it.
 *
 * The alternative was a copy on each side. kristianpeter.com already hand-rolled
 * exactly that copy (`VoiceAutomation.astro` + `.rise*` in its global.css) and
 * it is the reason this module exists: the shell is a product surface now, not
 * a thing each site reimplements.
 *
 * Constraints this file must keep:
 *   - No CSS custom properties from the app. A host page has no V4 tokens, so
 *     every colour and font here is a literal.
 *   - `--bzr-phone-h` / `--bzr-phone-crop` are declared BY the shell, not
 *     consumed from outside.
 *   - Class names are prefixed `bzr-` and must not collide on a host page.
 */

/** Stacking context. One above the corner orb iframe (2147483000) so the
 *  modal always covers it, and high enough to clear host-page chrome. */
export const PHONE_SHELL_Z_INDEX = 2147483001;

export const PHONE_SHELL_CLASS = {
  overlay: 'bzr-phone-overlay',
  /** The handset body — glass bezel, top-rounded, bottom-anchored. */
  bezel: 'bzr-phone',
  /** The screen inside the bezel. Owns the column layout and the scroll. */
  screen: 'bzr-phone-screen',
  island: 'bzr-phone-island',
  close: 'bzr-phone-close',
  /** Takes the slack in the column; whatever the consumer mounts goes here. */
  stage: 'bzr-phone-stage',
  meta: 'bzr-phone-meta',
  open: 'is-open',
} as const;

/**
 * The shell stylesheet.
 *
 * Ported from kristianpeter.com's `.rise*` rules, with three deliberate
 * departures from that source — each one a defect found by measuring the
 * rendered boxes rather than by eye:
 *
 *   1. The settle offset is `--bzr-phone-crop`, a length, not KP's bare `6%`.
 *      Percentage padding resolves against WIDTH, so a percentage could not
 *      reserve the height crop and the last line of content sat below the fold.
 *   2. The screen fill is ~0.88 alpha, not KP's 0.66–0.72. There the handset is
 *      a small object on a quiet page; at full-bleed mobile width the page
 *      headline read straight through the title.
 *   3. `.bzr-phone-stage` never clips. Consumers mount fixed-size boxes into it
 *      that can swap in much taller content (a post-call CTA card); an
 *      `overflow` here would swallow the tail without offering a scrollbar.
 *      The stage reserves the room and `.bzr-phone-screen` does the scrolling.
 */
export const PHONE_SHELL_CSS = `
.${PHONE_SHELL_CLASS.overlay} {
  position: fixed;
  inset: 0;
  z-index: ${PHONE_SHELL_Z_INDEX};
  display: flex;
  align-items: flex-end;
  justify-content: center;
  background: rgba(3, 6, 11, 0.62);
  backdrop-filter: blur(7px);
  -webkit-backdrop-filter: blur(7px);
  animation: bzr-phone-fade 200ms ease-out;
}
.${PHONE_SHELL_CLASS.bezel} {
  --bzr-phone-h: min(600px, 78vh);
  --bzr-phone-crop: calc(var(--bzr-phone-h) * 0.06);
  position: relative;
  width: min(332px, 86vw);
  height: var(--bzr-phone-h);
  padding: 11px 11px 0;
  border-radius: 52px 52px 0 0;
  background: linear-gradient(160deg, rgba(120, 150, 190, 0.22), rgba(20, 27, 42, 0.5) 55%, rgba(44, 55, 77, 0.3));
  backdrop-filter: blur(22px) saturate(1.2);
  -webkit-backdrop-filter: blur(22px) saturate(1.2);
  box-shadow:
    inset 0 0 0 1.5px rgba(160, 190, 230, 0.28),
    inset 0 0 0 4px rgba(6, 10, 18, 0.55),
    0 -30px 90px rgba(0, 0, 0, 0.55),
    0 0 70px rgba(34, 211, 238, 0.14);
  outline: none;
  text-align: center;
  box-sizing: border-box;
  transform: translateY(var(--bzr-phone-crop));
  animation: bzr-phone-rise 600ms cubic-bezier(0.16, 1, 0.3, 1);
}
.${PHONE_SHELL_CLASS.screen} {
  position: relative;
  height: 100%;
  box-sizing: border-box;
  border-radius: 42px 42px 0 0;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  background: radial-gradient(150% 90% at 50% 0%, rgba(10, 21, 36, 0.9) 0%, rgba(5, 10, 20, 0.87) 55%, rgba(3, 6, 12, 0.9) 100%);
  display: flex;
  flex-direction: column;
  padding: 48px 18px calc(var(--bzr-phone-crop) + 12px + env(safe-area-inset-bottom, 0px));
}
.${PHONE_SHELL_CLASS.island} {
  position: absolute;
  top: 12px;
  left: 50%;
  transform: translateX(-50%);
  width: 90px;
  height: 26px;
  border-radius: 20px;
  background: rgba(0, 0, 0, 0.85);
  z-index: 3;
}
.${PHONE_SHELL_CLASS.close} {
  position: absolute;
  top: 18px;
  right: 16px;
  z-index: 5;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  padding: 0;
  border-radius: 50%;
  border: 1px solid rgba(244, 244, 240, 0.18);
  background: rgba(6, 10, 18, 0.6);
  color: rgba(244, 244, 240, 0.66);
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  transition: color 0.15s, border-color 0.15s;
}
.${PHONE_SHELL_CLASS.close}:hover {
  color: #f4f4f0;
  border-color: #f4f4f0;
}
.${PHONE_SHELL_CLASS.stage} {
  position: relative;
  flex: 1 0 auto;
  min-height: 300px;
  display: grid;
  place-items: center;
}
.${PHONE_SHELL_CLASS.meta} {
  flex: none;
  padding: 0 4px;
}
.${PHONE_SHELL_CLASS.meta} small {
  display: block;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 9px;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  color: #22d3ee;
}
.${PHONE_SHELL_CLASS.meta} b {
  display: block;
  font-weight: 600;
  font-size: 21px;
  letter-spacing: -0.02em;
  margin: 8px 0 6px;
  color: #f4f4f0;
}
.${PHONE_SHELL_CLASS.meta} span {
  display: block;
  font-size: 13px;
  color: rgba(244, 244, 240, 0.66);
}
@keyframes bzr-phone-fade {
  from { opacity: 0; }
  to { opacity: 1; }
}
@keyframes bzr-phone-rise {
  from { transform: translateY(118%); }
  to { transform: translateY(var(--bzr-phone-crop)); }
}
@media (max-height: 620px) {
  .${PHONE_SHELL_CLASS.bezel} {
    --bzr-phone-h: 92vh;
    border-radius: 40px 40px 0 0;
  }
  .${PHONE_SHELL_CLASS.screen} {
    padding-top: 40px;
    border-radius: 32px 32px 0 0;
  }
}
@media (prefers-reduced-motion: reduce) {
  .${PHONE_SHELL_CLASS.overlay},
  .${PHONE_SHELL_CLASS.bezel} {
    animation: none !important;
  }
}
`.trim();
